/** Catalogue limits rendered as centimetres. Explicit MM is always divided.
 * Print.com labels its custom sizes "CM" while the numbers are millimetres
 * (Foto op Forex: 50-3010, Blackback up to 50000). Read as centimetres, nobody
 * could order Forex under 50 cm and 30 m was accepted (16-10-2026). Every live
 * "CM" record has values above 300, no record from another supplier does, so
 * above 300 the label is ignored, exactly like the unitless heuristic. */
export function normalizeCustomSizeLimits(customSizes: {
  minWidth?: unknown; maxWidth?: unknown; minHeight?: unknown; maxHeight?: unknown; sizeUnit?: unknown;
} | null | undefined) {
  const numbers = [customSizes?.minWidth, customSizes?.maxWidth, customSizes?.minHeight, customSizes?.maxHeight]
    .map((value) => Number(value));
  const unit = String(customSizes?.sizeUnit || '').trim().toUpperCase();
  const largest = Math.max(...numbers.map((value) => Number.isFinite(value) ? value : 0));
  const divisor = unit === 'MM' || largest > 300 ? 10 : 1;
  const values = numbers.map((value) => Number.isFinite(value) && value > 0 ? value / divisor : undefined);
  return { minWidth: values[0], maxWidth: values[1], minHeight: values[2], maxHeight: values[3] };
}

/** Physical variant intervals in Print.com's excludes are millimetres, even
 * when the catalogue's base size says CM. Only matching option conditions
 * apply. Technique-specific limits belong to automatic method selection and
 * must not reject a size supported by another technique. */
export function customSizeLimitsForConfiguration(
  customSizes: Parameters<typeof normalizeCustomSizeLimits>[0],
  configuration: Record<string, unknown>,
  excludes: unknown,
) {
  const limits = normalizeCustomSizeLimits(customSizes);
  if (!Array.isArray(excludes)) return limits;
  for (const rule of excludes) {
    if (!Array.isArray(rule) || rule.some((condition) => condition?.property === 'printingmethod')) continue;
    const conditions = rule.filter((condition) => condition?.property !== 'size');
    if (!conditions.every((condition) => Array.isArray(condition?.options) &&
      configuration[condition.property] !== undefined && condition.options.map(String).includes(String(configuration[condition.property])))) continue;
    for (const condition of rule.filter((condition) => condition?.property === 'size')) {
      for (const value of Array.isArray(condition.options) ? condition.options : []) {
        const match = /^custom:(width|height):(\d*(?:\.\d+)?)-(\d*(?:\.\d+)?)$/.exec(String(value));
        if (!match) continue;
        const minKey = match[1] === 'width' ? 'minWidth' : 'minHeight';
        const maxKey = match[1] === 'width' ? 'maxWidth' : 'maxHeight';
        if (match[2]) limits[minKey] = Math.max(limits[minKey] ?? 0, Number(match[2]) / 10);
        if (match[3]) limits[maxKey] = Math.min(limits[maxKey] ?? Infinity, Number(match[3]) / 10);
      }
    }
  }
  return limits;
}
